// Pure helpers behind the approval-policy editor (ApprovalPolicyTab). Kept out of the
// component so the rules that decide what is SENT — the Finance floor, the Group Finance
// stage's invariants, the threshold currency — are tested without rendering.
//
// The editor is row-based, not key-based: a stored policy may repeat a level with distinct
// effective_from dates, and a key-based draft would silently drop every row but one on save.
import type {
  ApprovalLevel,
  ApprovalLevelKey,
  ApprovalPolicy,
} from '@/services/payrollSettingsService';

/** Chain order, as the server enforces it. */
export const LEVEL_ORDER: ApprovalLevelKey[] = ['hr', 'finance', 'group', 'final'];

export const LEVEL_LABEL: Record<ApprovalLevelKey, string> = {
  hr: 'HR pre-approval',
  finance: 'Finance approval',
  group: 'Group Finance approval',
  final: 'Final sign-off',
};

export const LEVEL_HINT: Record<ApprovalLevelKey, string> = {
  hr: 'Optional first step — HR reviews the run before Finance.',
  finance: 'Mandatory independent review. Always required and always a distinct second person.',
  group:
    'Optional escalation after Finance — someone with access to every company (Group Finance) signs off, usually only above an amount.',
  final: 'Optional last step — a final sign-off after Finance, e.g. for large runs.',
};

export interface LevelRow {
  rowId: string;
  key: ApprovalLevelKey;
  label: string;
  role_key: string;
  distinct: boolean;
  /** '' = no threshold. */
  min_amount: string;
  /** Kept from the stored row; the UI never edits it (thresholds are in the base currency). */
  currency: string;
  /** '' = always in force. */
  effective_from: string;
  scope: 'entity' | 'group';
  substitute_allowed: boolean;
}

let seq = 0;
const nextId = () => `lvl-${++seq}`;

const orderOf = (k: ApprovalLevelKey) => {
  const i = LEVEL_ORDER.indexOf(k);
  return i < 0 ? LEVEL_ORDER.length : i;
};

/** Canonical order, then undated before dated, then by date. */
export function sortRows(rows: LevelRow[]): LevelRow[] {
  return [...rows].sort(
    (a, b) =>
      orderOf(a.key) - orderOf(b.key) ||
      (a.effective_from || '').localeCompare(b.effective_from || ''),
  );
}

function blankRow(key: ApprovalLevelKey): LevelRow {
  return {
    rowId: nextId(),
    key,
    label: LEVEL_LABEL[key],
    role_key: '',
    distinct: key === 'finance' || key === 'group',
    min_amount: '',
    currency: '',
    effective_from: '',
    scope: key === 'group' ? 'group' : 'entity',
    substitute_allowed: false,
  };
}

/** Seed editable rows from a stored/effective chain. Finance is always present. */
export function seedRows(levels: ApprovalLevel[] | null | undefined): LevelRow[] {
  const rows: LevelRow[] = (levels ?? [])
    .filter((l) => LEVEL_ORDER.includes(l.key))
    // A level stored as not-required never applies; the editor only shows levels in use.
    .filter((l) => l.key === 'finance' || l.required !== false)
    .map((l) => ({
      rowId: nextId(),
      key: l.key,
      label: l.label || LEVEL_LABEL[l.key],
      role_key: l.role_key ?? '',
      distinct: l.key === 'finance' || l.key === 'group' ? true : !!l.distinct,
      min_amount: l.key === 'finance' || l.min_amount == null ? '' : String(l.min_amount),
      currency: l.currency ?? '',
      effective_from: l.key === 'finance' ? '' : l.effective_from ?? '',
      scope: l.key === 'group' || l.scope === 'group' ? 'group' : 'entity',
      substitute_allowed: l.key === 'group' || l.scope === 'group' ? false : !!l.substitute_allowed,
    }));
  if (!rows.some((r) => r.key === 'finance')) rows.push(blankRow('finance'));
  return sortRows(rows);
}

/** Add a level at its canonical position. Finance can't be added twice. */
export function addLevel(rows: LevelRow[], key: ApprovalLevelKey): LevelRow[] {
  if (key === 'finance' && rows.some((r) => r.key === 'finance')) return rows;
  return sortRows([...rows, blankRow(key)]);
}

/** Remove a row. The Finance floor is never removable. */
export function removeLevel(rows: LevelRow[], rowId: string): LevelRow[] {
  return rows.filter((r) => r.rowId !== rowId || r.key === 'finance');
}

export function patchRow(rows: LevelRow[], rowId: string, patch: Partial<LevelRow>): LevelRow[] {
  return sortRows(rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
}

/**
 * Build the PUT chain. The server is the authority on every rule below; applying them here
 * means what the admin SEES is what is saved, instead of a 400 after pressing Save.
 *   • Finance: required, distinct, no threshold, no date.
 *   • Group Finance: always group-scoped, distinct, never a stand-in.
 *   • A group-scoped level can't also be "Group Finance may stand in".
 *   • A threshold on a company policy is in the company's base currency (no FX); on the
 *     group default it keeps whatever currency it was stored with (none = each company's own).
 */
export function buildLevels(rows: LevelRow[], baseCurrency?: string | null): ApprovalLevel[] {
  return sortRows(rows).map((r) => {
    const isFin = r.key === 'finance';
    const groupScoped = r.key === 'group' || r.scope === 'group';
    const amount = isFin ? '' : r.min_amount.trim();
    const currency = amount ? (baseCurrency ? baseCurrency.toUpperCase() : r.currency || undefined) : undefined;
    const level: ApprovalLevel = {
      key: r.key,
      label: r.label.trim() || LEVEL_LABEL[r.key],
      required: true,
      role_key: r.role_key,
      distinct: isFin || r.key === 'group' ? true : r.distinct,
      min_amount: amount ? amount : null,
      scope: groupScoped ? 'group' : 'entity',
      substitute_allowed: groupScoped ? false : r.substitute_allowed,
    };
    if (currency) level.currency = currency;
    if (!isFin && r.effective_from) level.effective_from = r.effective_from;
    return level;
  });
}

/** The first level not bound to a role — the server rejects it; say so before Save. */
export function firstUnboundLevel(rows: LevelRow[]): LevelRow | undefined {
  return sortRows(rows).find((r) => !r.role_key);
}

/** Whether the chain already has a row for `key` (the "Add" buttons offer only absent levels). */
export const hasLevel = (rows: LevelRow[], key: ApprovalLevelKey) => rows.some((r) => r.key === key);

export type PolicyStatusTone = 'info' | 'success' | 'neutral';

/**
 * How the selected policy is described to the admin. `forCompany` = a company is selected
 * (vs the company-group default).
 */
export function policyStatus(
  p: Pick<ApprovalPolicy, 'source' | 'configured'>,
  forCompany: boolean,
): { label: string; detail: string; tone: PolicyStatusTone } {
  if (forCompany) {
    if (p.source === 'entity') {
      return {
        label: 'Custom for this company',
        detail: 'This company has its own approval chain. Reverting makes it use the company-group default again.',
        tone: 'success',
      };
    }
    if (p.source === 'tenant') {
      return {
        label: 'Uses the company-group default',
        detail: 'This company has no chain of its own. Saving here gives it one; the group default is unchanged.',
        tone: 'info',
      };
    }
    return {
      label: 'Built-in default',
      detail: 'Neither this company nor the group has configured a chain, so the built-in HR → Finance → Final chain applies.',
      tone: 'neutral',
    };
  }
  if (p.source === 'tenant' && p.configured) {
    return {
      label: 'Company-group default',
      detail: 'Applies to every company that has no chain of its own.',
      tone: 'success',
    };
  }
  return {
    label: 'Built-in default',
    detail: 'No group default is configured, so the built-in HR → Finance → Final chain applies to every company without its own.',
    tone: 'neutral',
  };
}
